import { z } from 'zod';
import {
  AllocateReservationBody,
  AllocateReservationResponse,
  CancelReservationBody,
  CancelReservationResponse,
  CheckInReservationResponse,
  CompleteReservationResponse,
  CreateReservationBody,
  CreateReservationResponse,
  ErrorResponse,
  ResourceOccupancyResponse,
  ServiceConstraintsResponse,
  SuggestAvailabilityBody,
  SuggestAvailabilityResponse,
  TodayReservationRow,
  VenueZonesResponse,
  type ZoneSummary,
} from '@ubc/ui-contracts';
import {
  API_WORKER_URL,
  BFF_WORKER_URL,
  venueSlugFromBrowserHost,
  withVenueHeader as ssotWithVenueHeader,
} from '@shared-endpoints';

const DashboardResponse = z.array(TodayReservationRow);

export type DashboardRow = z.infer<typeof TodayReservationRow>;
export type Zone = ZoneSummary;
export type NewReservation = z.infer<typeof CreateReservationBody>;
export type CreateResult = z.infer<typeof CreateReservationResponse>;
export type OccupancyView = z.infer<typeof ResourceOccupancyResponse>;
export type ServiceConstraints = z.infer<typeof ServiceConstraintsResponse>;
export type CandidateSegment = z.infer<
  typeof SuggestAvailabilityResponse
>['candidateSegments'][number];
export type AllocateResult = z.infer<typeof AllocateReservationResponse>;
export type SuggestResult = z.infer<typeof SuggestAvailabilityResponse>;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Two distinct base URLs from @shared-endpoints SSOT:
 *   - VITE_API_BASE_URL  → core write/read worker (ubc-api).
 *   - VITE_BFF_BASE_URL  → operations-console BFF (ubc-bff-ops).
 *
 * Resolution: env var > prod defaults from SSOT > '' (dev Vite proxy).
 * Previously hardcoded in this file — drift risk. Now single-source.
 */
const rawApi = import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.PROD ? API_WORKER_URL : '');
const rawBff =
  import.meta.env.VITE_BFF_BASE_URL ?? (import.meta.env.PROD ? BFF_WORKER_URL : rawApi);
const API_BASE = rawApi.replace(/\/$/, '');
const BFF_BASE = rawBff.replace(/\/$/, '');

/** Write / mutation endpoints (POST / PUT / DELETE) — always go to core worker. */
function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/** Read endpoints (GET) — go to BFF when configured, falls back to core. */
function readUrl(path: string): string {
  return `${BFF_BASE}${path}`;
}

/**
 * Cross-origin requests to the API worker include the Access cookie so the
 * worker's JWT verification can attribute the actor. Safe for same-origin too.
 */
const CREDENTIALS: RequestCredentials = 'include';

/**
 * Venue-slug header injection delegates to @shared-endpoints so portal
 * and ops-console share one path. Thin local wrapper passes the
 * Vite dev fallback env var; rest of the file calls withVenueHeader().
 */
function withVenueHeader(init: RequestInit = {}): RequestInit {
  const slug = venueSlugFromBrowserHost({ devFallback: import.meta.env.VITE_DEV_VENUE_SLUG });
  return ssotWithVenueHeader(init, slug);
}

// `z.ZodType<T>` binds T to the INPUT type, so `.default()` fields still
// read as optional on the caller side. We want the parsed/OUTPUT shape
// (post-default coercion), which is what `z.output<S>` returns.
async function handleResponse<S extends z.ZodTypeAny>(
  res: Response,
  schema: S,
): Promise<z.output<S>> {
  const raw: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const err = ErrorResponse.safeParse(raw);
    if (err.success) {
      throw new ApiError(res.status, err.data.code, err.data.message, err.data.details);
    }
    throw new ApiError(res.status, 'UNKNOWN', `HTTP ${res.status}`);
  }
  return schema.parse(raw) as z.output<S>;
}

async function jsonRequest<T>(
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
): Promise<T> {
  const init: RequestInit = withVenueHeader({
    method,
    headers: { 'content-type': 'application/json' },
    credentials: CREDENTIALS,
  });
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const res = await fetch(apiUrl(path), init);
  return handleResponse(res, schema);
}

async function postJson<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  return jsonRequest('POST', path, body, schema);
}

async function putJson<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  return jsonRequest('PUT', path, body, schema);
}

/**
 * Untyped variants for endpoints whose response is currently narrowed
 * via an `as X` cast rather than a Zod schema. Callers apply the cast
 * at the call site — same safety as before, just without 10 lines of
 * fetch+error-handler boilerplate per call. Future cleanup should give
 * each endpoint a proper schema (move to `putJson` / `postJson` with
 * typed Zod).
 */
const RawJsonSchema = z.unknown();

async function putJsonRaw(path: string, body: unknown): Promise<unknown> {
  return jsonRequest('PUT', path, body, RawJsonSchema);
}

async function postJsonRaw(path: string, body: unknown): Promise<unknown> {
  return jsonRequest('POST', path, body, RawJsonSchema);
}

export interface DashboardParams {
  readonly venueId: string;
  readonly today: string;
  readonly tz: string;
}

export async function fetchDashboard(p: DashboardParams): Promise<DashboardRow[]> {
  const qs = new URLSearchParams({ venueId: p.venueId, today: p.today, tz: p.tz });
  const res = await fetch(readUrl(`/api/staff/dashboard?${qs.toString()}`), withVenueHeader({ credentials: CREDENTIALS }));
  return handleResponse(res, DashboardResponse);
}

export async function createReservation(body: NewReservation): Promise<CreateResult> {
  CreateReservationBody.parse(body);
  return postJson('/api/staff/reservations', body, CreateReservationResponse);
}

export interface OccupancyParams {
  readonly resourceId: string;
  readonly startTime: string;
  readonly endTime: string;
}

export async function fetchResourceOccupancy(p: OccupancyParams): Promise<OccupancyView> {
  const qs = new URLSearchParams({
    resourceId: p.resourceId,
    start: p.startTime,
    end: p.endTime,
  });
  const res = await fetch(readUrl(`/api/staff/resource-occupancy?${qs.toString()}`), withVenueHeader({ credentials: CREDENTIALS }));
  return handleResponse(res, ResourceOccupancyResponse);
}

export async function suggestAvailability(
  body: z.infer<typeof SuggestAvailabilityBody>,
): Promise<SuggestResult> {
  SuggestAvailabilityBody.parse(body);
  return postJson(
    '/api/staff/reservations/suggest-availability',
    body,
    SuggestAvailabilityResponse,
  );
}

export async function allocateReservation(
  reservationId: string,
  body: z.infer<typeof AllocateReservationBody>,
): Promise<AllocateResult> {
  AllocateReservationBody.parse(body);
  return postJson(
    `/api/staff/reservations/${encodeURIComponent(reservationId)}/allocate`,
    body,
    AllocateReservationResponse,
  );
}

export async function checkInReservation(reservationId: string) {
  return postJson(
    `/api/staff/reservations/${encodeURIComponent(reservationId)}/check-in`,
    undefined,
    CheckInReservationResponse,
  );
}

export async function completeReservation(reservationId: string) {
  return postJson(
    `/api/staff/reservations/${encodeURIComponent(reservationId)}/complete`,
    undefined,
    CompleteReservationResponse,
  );
}

export type PricingUnit =
  | 'per_hour_per_person'
  | 'per_hour_per_seat'
  | 'flat_per_hour'
  | 'tiered_per_person';

export interface PartySizeTierInput {
  minPartySize: number;
  maxPartySize: number | null;
  amountPerPersonPerHour: number;
}

export interface ZonePricingInput {
  amount: number;
  unit: PricingUnit;
  currency: 'TWD';
  tiers?: PartySizeTierInput[] | null;
}

export interface UpdateServicePolicyInput {
  minPartySize: number;
  maxPartySize: number;
  durationMinMinutes: number;
  durationMaxMinutes: number;
  leadTimeMinutes: number;
  blackoutWindows: Array<{ start: string; end: string }>;
  overbookAllowed: boolean;
  splitAllowed: boolean;
  extensionAllowed: boolean;
}

export async function updateServicePolicy(
  serviceId: string,
  body: UpdateServicePolicyInput,
): Promise<{ serviceId: string; policySetId: string; version: number }> {
  return (await putJsonRaw(
    `/api/staff/services/${encodeURIComponent(serviceId)}/policy`,
    body,
  )) as { serviceId: string; policySetId: string; version: number };
}

export interface StatusCounts {
  pending: number;
  confirmed: number;
  checked_in: number;
  completed: number;
  canceled: number;
  no_show: number;
}

export interface DailyCountBucket {
  dateLocal: string;
  total: number;
  byStatus: StatusCounts;
  paidRevenueTwd: number;
  quotedRevenueTwd: number;
}

export interface PartySizeBucket {
  min: number;
  max: number | null;
  label: string;
  count: number;
}

export interface ReservationAnalytics {
  totalReservations: number;
  activeReservations: number;
  expectedGuests: number;
  canceledCount: number;
  noShowCount: number;
  cancelRatePct: number;
  daily: DailyCountBucket[];
  partySizeBuckets: PartySizeBucket[];
  statusTotals: StatusCounts;
  paidRevenueTwd: number;
  quotedRevenueTwd: number;
}

export interface UpdateReservationInput {
  partySize?: number;
  notes?: string | null;
}

export interface UpdateReservationResult {
  reservationId: string;
  partySize: number;
  notes: string | null;
  status: 'pending' | 'confirmed' | 'checked_in' | 'completed' | 'canceled' | 'no_show';
}

export async function updateReservation(
  reservationId: string,
  body: UpdateReservationInput,
): Promise<UpdateReservationResult> {
  return (await putJsonRaw(
    `/api/staff/reservations/${encodeURIComponent(reservationId)}`,
    body,
  )) as UpdateReservationResult;
}

export type ReservationStatus =
  | 'pending'
  | 'confirmed'
  | 'checked_in'
  | 'completed'
  | 'canceled'
  | 'no_show';

export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';

export async function markReservationNoShow(
  reservationId: string,
  reason?: string,
): Promise<{ reservationId: string; status: 'no_show' }> {
  const body: Record<string, string> = {};
  if (reason) body['reason'] = reason;
  return (await postJsonRaw(`/api/staff/reservations/${encodeURIComponent(reservationId)}/no-show`, body)) as { reservationId: string; status: 'no_show' };
}

export interface ReservationListRow {
  reservationId: string;
  serviceId: string;
  scenarioType: string;
  status: ReservationStatus;
  partySize: number;
  startTime: string;
  endTime: string;
  source: string;
  customerRef: string | null;
  customerId: string | null;
  customerName: string | null;
  notes: string | null;
  totalAmount: number | null;
  paidAmount: number | null;
  currency: 'TWD' | null;
  paymentStatus: PaymentStatus;
  paidAt: string | null;
}

export interface RecordPaymentInput {
  paidAmount: number;
  currency: 'TWD';
  paidAt: string;
}

export interface RecordPaymentResult {
  reservationId: string;
  totalAmount: number | null;
  paidAmount: number;
  currency: 'TWD';
  paymentStatus: PaymentStatus;
  paidAt: string;
}

export async function recordReservationPayment(
  reservationId: string,
  input: RecordPaymentInput,
): Promise<RecordPaymentResult> {
  return (await postJsonRaw(`/api/staff/reservations/${encodeURIComponent(reservationId)}/record-payment`, input)) as RecordPaymentResult;
}

export interface ListReservationsParams {
  venueId: string;
  start: string;
  end: string;
  statuses?: ReservationStatus[];
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listReservations(
  p: ListReservationsParams,
): Promise<{ items: ReservationListRow[]; total: number }> {
  const qs = new URLSearchParams({
    venueId: p.venueId,
    start: p.start,
    end: p.end,
  });
  if (p.statuses && p.statuses.length > 0) qs.set('statuses', p.statuses.join(','));
  if (p.search && p.search.trim().length > 0) qs.set('search', p.search.trim());
  if (p.limit !== undefined) qs.set('limit', String(p.limit));
  if (p.offset !== undefined) qs.set('offset', String(p.offset));
  const res = await fetch(readUrl(`/api/staff/reservations?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `listReservations: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as { items: ReservationListRow[]; total: number };
}

export type ActorRole = 'guest' | 'staff' | 'manager' | 'admin' | 'system';

export interface AuditLogRow {
  auditId: string;
  action: string;
  actorId: string;
  actorRole: ActorRole;
  targetType: string;
  targetId: string;
  payload: Record<string, unknown>;
  createdAtIso: string;
}

export interface ListAuditLogsParams {
  start: string;
  end: string;
  targetType?: string;
  action?: string;
  limit?: number;
  offset?: number;
}

export async function listAuditLogs(
  p: ListAuditLogsParams,
): Promise<{ items: AuditLogRow[]; total: number }> {
  const qs = new URLSearchParams({ start: p.start, end: p.end });
  if (p.targetType && p.targetType.length > 0) qs.set('targetType', p.targetType);
  if (p.action && p.action.length > 0) qs.set('action', p.action);
  if (p.limit !== undefined) qs.set('limit', String(p.limit));
  if (p.offset !== undefined) qs.set('offset', String(p.offset));
  const res = await fetch(readUrl(`/api/staff/audit-logs?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `listAuditLogs: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as { items: AuditLogRow[]; total: number };
}

export async function fetchReservationAnalytics(p: {
  venueId: string;
  start: string;
  end: string;
  tz: string;
}): Promise<ReservationAnalytics> {
  const qs = new URLSearchParams({
    venueId: p.venueId,
    start: p.start,
    end: p.end,
    tz: p.tz,
  });
  const res = await fetch(readUrl(`/api/staff/analytics/reservations?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `fetchReservationAnalytics: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as ReservationAnalytics;
}

export interface VenueInfoSummary {
  venueId: string;
  name: string;
  timezone: string;
  status: 'active' | 'suspended';
}

export async function getVenueInfo(venueId: string): Promise<VenueInfoSummary> {
  const qs = new URLSearchParams({ venueId });
  const res = await fetch(readUrl(`/api/staff/venue-info?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `getVenueInfo: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as VenueInfoSummary;
}

export async function updateVenueInfo(
  venueId: string,
  body: { name: string; timezone: string; status: 'active' | 'suspended' },
): Promise<VenueInfoSummary> {
  const qs = new URLSearchParams({ venueId });
  return (await putJsonRaw(`/api/staff/venue-info?${qs.toString()}`, body)) as VenueInfoSummary;
}

export interface ResourceUnitSummary {
  unitId: string;
  resourceId: string;
  position: number;
  label: string;
  capacity: number;
  active: boolean;
  minCapacity: number | null;
}

export async function listResourceUnits(resourceId: string): Promise<ResourceUnitSummary[]> {
  const res = await fetch(readUrl(`/api/staff/venue-zones/${encodeURIComponent(resourceId)}/units`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `listResourceUnits: HTTP ${res.status}`,
    );
  }
  const body = (await res.json()) as { units: ResourceUnitSummary[] };
  return body.units;
}

export async function updateResourceUnit(
  unitId: string,
  body: { label: string; capacity: number; active: boolean; minCapacity: number | null },
): Promise<ResourceUnitSummary> {
  return (await putJsonRaw(`/api/staff/resource-units/${encodeURIComponent(unitId)}`, body)) as ResourceUnitSummary;
}

export async function updateZoneMetadata(
  resourceId: string,
  body: { zoneLabel: string; capacity: number; active: boolean },
): Promise<{ resourceId: string; zoneLabel: string; capacity: number; active: boolean }> {
  return (await putJsonRaw(`/api/staff/venue-zones/${encodeURIComponent(resourceId)}/metadata`, body)) as { resourceId: string; zoneLabel: string; capacity: number; active: boolean };
}

export async function updateServiceInfo(
  serviceId: string,
  body: { name: string; active: boolean },
): Promise<{ serviceId: string; name: string; active: boolean }> {
  return (await putJsonRaw(`/api/staff/services/${encodeURIComponent(serviceId)}/info`, body)) as { serviceId: string; name: string; active: boolean };
}

export async function updateZonePricing(
  resourceId: string,
  pricing: ZonePricingInput | null,
): Promise<{ resourceId: string; pricing: ZonePricingInput | null }> {
  return (await putJsonRaw(`/api/staff/venue-zones/${encodeURIComponent(resourceId)}/pricing`, { pricing })) as { resourceId: string; pricing: ZonePricingInput | null };
}

export async function fetchVenueZones(venueId: string): Promise<Zone[]> {
  const qs = new URLSearchParams({ venueId });
  const res = await fetch(readUrl(`/api/staff/venue-zones?${qs.toString()}`), withVenueHeader({ credentials: CREDENTIALS }));
  const parsed = await handleResponse(res, VenueZonesResponse);
  return [...parsed.zones];
}

export async function fetchServiceConstraints(
  venueId: string,
  serviceId: string,
): Promise<ServiceConstraints> {
  const qs = new URLSearchParams({ venueId, serviceId });
  const res = await fetch(readUrl(`/api/staff/service-constraints?${qs.toString()}`), withVenueHeader({ credentials: CREDENTIALS }));
  return handleResponse(res, ServiceConstraintsResponse);
}

export async function cancelReservation(reservationId: string, reason: string | null) {
  const body: z.infer<typeof CancelReservationBody> = { reason };
  return postJson(
    `/api/staff/reservations/${encodeURIComponent(reservationId)}/cancel`,
    body,
    CancelReservationResponse,
  );
}

export const TakeoutRequestView = z.object({
  requestId: z.string(),
  pickupTimeIso: z.string(),
  status: z.enum(['submitted', 'ready', 'completed', 'cancelled']),
  customerRef: z.string(),
});
export type TakeoutRequestView = z.infer<typeof TakeoutRequestView>;
const TakeoutListResponse = z.array(TakeoutRequestView);

export type NotificationChannel = 'email' | 'line';
export type NotificationStatus = 'pending_provider' | 'sent' | 'failed';

export interface NotificationLogRow {
  notificationId: string;
  eventId: string;
  channel: NotificationChannel;
  recipient: string;
  template: string;
  status: NotificationStatus;
  scheduledAtIso: string;
  sentAtIso: string | null;
  lastError: string | null;
  payload: Record<string, unknown>;
}

export async function listNotificationLogs(params: {
  channel?: NotificationChannel;
  status?: NotificationStatus;
  limit?: number;
  offset?: number;
}): Promise<{ items: NotificationLogRow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.channel) qs.set('channel', params.channel);
  if (params.status) qs.set('status', params.status);
  qs.set('limit', String(params.limit ?? 50));
  qs.set('offset', String(params.offset ?? 0));
  const res = await fetch(readUrl(`/api/staff/notification-logs?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `listNotificationLogs: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as { items: NotificationLogRow[]; total: number };
}

export async function fetchStaffTakeoutRequests(venueId: string): Promise<TakeoutRequestView[]> {
  const qs = new URLSearchParams({ venueId });
  const res = await fetch(readUrl(`/api/staff/takeout-requests?${qs.toString()}`), withVenueHeader({ credentials: CREDENTIALS }));
  return handleResponse(res, TakeoutListResponse);
}

export interface StaffVenueProfile {
  slug: string;
  venueId: string;
  name: string;
  timezone: string;
  branding: { appTitle: string; tagline: string; tileUnitLabel: string };
  capabilities: {
    allocation: { enabled: true; scenarios: readonly string[] };
    takeout: { enabled: boolean };
    publicPortal: { enabled: boolean; showTakeoutForm: boolean; lineId?: string };
    opsConsole: { showTakeoutPanel: boolean };
    billing: { enabled: boolean; autoQuoteOnAllocate: boolean };
    customerCrm: {
      enabled: boolean;
      statsAggregation: boolean;
      autoTier: boolean;
      preferences: boolean;
      segmentation: boolean;
    };
    notifications: { enabled: boolean; channels: readonly ('email' | 'line')[] };
  };
  zones: Array<{
    serviceId: string;
    resourceId: string;
    policySetId: string;
    scenarioType: string;
    name: string;
    zoneLabel: string;
    capacity: number;
    minPartySize: number;
    maxPartySize: number;
    unitCount: number;
    pricing: { amount: number; unit: string; currency: string } | null;
  }>;
}

export async function fetchStaffVenueProfile(): Promise<StaffVenueProfile> {
  const res = await fetch(readUrl('/api/staff/venue-profile'), withVenueHeader({ credentials: CREDENTIALS }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `venue-profile: HTTP ${res.status}`);
  }
  return (await res.json()) as StaffVenueProfile;
}

// ── Customers ───────────────────────────────────────────────────────────────

export type CustomerTier = 'normal' | 'vip' | 'blacklist';
export type CustomerTierReason = 'auto' | 'manual';

export interface CustomerStatsSummary {
  visitCount: number;
  noShowCount: number;
  cancellationCount: number;
  lastVisitEpoch: number | null;
  avgPartySize: number;
  totalSpentTwd: number;
}

export interface CustomerSummary {
  customerId: string;
  venueId: string;
  displayName: string;
  contactMethods: Array<{ kind: 'phone' | 'line' | 'email'; value: string; verified: boolean }>;
  tags: string[];
  note: string | null;
  archivedAt: number | null;
  stats: CustomerStatsSummary;
  tier: CustomerTier;
  tierReason: CustomerTierReason;
  createdAt: number;
  updatedAt: number;
}

export interface MonthDayDto {
  month: number;
  day: number;
}

export interface CustomerPreferencesUpdate {
  birthday?: MonthDayDto | null;
  anniversary?: MonthDayDto | null;
  seatingPreference?: string | null;
  dietaryRestrictions?: string[];
}

export async function updateCustomerPreferences(
  customerId: string,
  body: CustomerPreferencesUpdate,
): Promise<{
  customerId: string;
  birthday: MonthDayDto | null;
  anniversary: MonthDayDto | null;
  seatingPreference: string | null;
  dietaryRestrictions: string[];
}> {
  return (await putJsonRaw(`/api/staff/customers/${encodeURIComponent(customerId)}/preferences`, body)) as {
    customerId: string;
    birthday: MonthDayDto | null;
    anniversary: MonthDayDto | null;
    seatingPreference: string | null;
    dietaryRestrictions: string[];
  };
}

export interface UpcomingBirthdayRow {
  customerId: string;
  displayName: string;
  birthday: MonthDayDto;
}

export async function listUpcomingBirthdays(
  venueId: string,
  windowDays = 30,
  today?: string,
): Promise<UpcomingBirthdayRow[]> {
  const qs = new URLSearchParams({ venueId, windowDays: String(windowDays) });
  if (today) qs.set('today', today);
  const res = await fetch(readUrl(`/api/staff/customers/upcoming-birthdays?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `listUpcomingBirthdays: HTTP ${res.status}`,
    );
  }
  const body = (await res.json()) as { items: UpcomingBirthdayRow[] };
  return body.items;
}

export interface CustomerSegmentFilters {
  tags?: string[];
  tier?: CustomerTier[];
  visitCountMin?: number;
  visitCountMax?: number;
  lastVisitWithinDays?: number;
  hasBirthdayWithinDays?: number;
}

export interface CustomerSegmentRow {
  customerId: string;
  displayName: string;
  tags: string[];
  tier: CustomerTier;
  visitCount: number;
  lastVisitIso: string | null;
  birthday: MonthDayDto | null;
  totalSpentTwd: number;
}

export interface ListCustomerSegmentsParams {
  venueId: string;
  filters: CustomerSegmentFilters;
  limit?: number;
  offset?: number;
  includeArchived?: boolean;
  today?: string;
}

export async function listCustomerSegments(
  params: ListCustomerSegmentsParams,
): Promise<{ items: CustomerSegmentRow[]; total: number }> {
  const body: Record<string, unknown> = {
    venueId: params.venueId,
    filters: params.filters,
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
    includeArchived: params.includeArchived ?? false,
  };
  if (params.today) body['today'] = params.today;
  return (await postJsonRaw('/api/staff/customers/segments', body)) as { items: CustomerSegmentRow[];
 total: number };
}

export async function setCustomerTier(
  customerId: string,
  body: { mode: 'manual'; tier: CustomerTier } | { mode: 'clear' },
): Promise<{ customerId: string; tier: CustomerTier; tierReason: CustomerTierReason }> {
  return (await putJsonRaw(`/api/staff/customers/${encodeURIComponent(customerId)}/tier`, body)) as {
    customerId: string;
    tier: CustomerTier;
    tierReason: CustomerTierReason;
  };
}

export interface ListCustomersParams {
  readonly venueId: string;
  readonly search?: string;
  readonly tiers?: readonly CustomerTier[];
  readonly limit?: number;
  readonly offset?: number;
}

export interface CustomerListItemSummary {
  customer: CustomerSummary;
  reservationCount: number;
  lastVisitIso: string | null;
}

export async function listCustomers(
  p: ListCustomersParams,
): Promise<{ items: CustomerListItemSummary[]; total: number }> {
  const qs = new URLSearchParams({ venueId: p.venueId });
  if (p.search) qs.set('search', p.search);
  if (p.tiers && p.tiers.length > 0) qs.set('tiers', p.tiers.join(','));
  qs.set('limit', String(p.limit ?? 20));
  qs.set('offset', String(p.offset ?? 0));
  const res = await fetch(readUrl(`/api/staff/customers?${qs.toString()}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `listCustomers: HTTP ${res.status}`);
  }
  return (await res.json()) as { items: CustomerListItemSummary[]; total: number };
}

export async function getCustomer(customerId: string): Promise<CustomerSummary | null> {
  const res = await fetch(readUrl(`/api/staff/customers/${encodeURIComponent(customerId)}`), withVenueHeader({
    credentials: CREDENTIALS,
  }));
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `getCustomer: HTTP ${res.status}`);
  }
  return (await res.json()) as CustomerSummary;
}

export interface UpsertCustomerInput {
  customerId?: string;
  venueId: string;
  displayName: string;
  contactMethods: Array<{ kind: 'phone' | 'line' | 'email'; value: string; verified?: boolean }>;
  tags?: string[];
  note?: string | null;
}

export async function upsertCustomer(
  body: UpsertCustomerInput,
): Promise<{ customerId: string; created: boolean }> {
  return (await postJsonRaw('/api/staff/customers', body)) as { customerId: string;
 created: boolean };
}

export interface CustomerReservationSummary {
  reservationId: string;
  serviceId: string;
  scenarioType: string;
  partySize: number;
  startTimeIso: string;
  endTimeIso: string;
  status: string;
  notes: string | null;
}

export async function mergeCustomers(
  primaryId: string,
  secondaryId: string,
): Promise<{ primaryId: string; secondaryId: string; reservationsMoved: number; archivedAt: string }> {
  return (await postJsonRaw(`/api/staff/customers/${encodeURIComponent(primaryId)}/merge`, { secondaryId })) as {
    primaryId: string;
    secondaryId: string;
    reservationsMoved: number;
    archivedAt: string;
  };
}

export async function archiveCustomer(
  customerId: string,
): Promise<{ customerId: string; archivedAt: string }> {
  return (await postJsonRaw(`/api/staff/customers/${encodeURIComponent(customerId)}/archive`, undefined)) as { customerId: string; archivedAt: string };
}

export async function listCustomerReservations(
  customerId: string,
  venueId: string,
): Promise<{ items: CustomerReservationSummary[]; total: number }> {
  const qs = new URLSearchParams({ venueId });
  const res = await fetch(
    readUrl(`/api/staff/customers/${encodeURIComponent(customerId)}/reservations?${qs.toString()}`),
    withVenueHeader({ credentials: CREDENTIALS }),
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `listCustomerReservations: HTTP ${res.status}`);
  }
  return (await res.json()) as { items: CustomerReservationSummary[]; total: number };
}

// ── Venue settings (§Region B) ─────────────────────────────────────────────

export interface VenueSettingsView {
  venueId: string;
  notification: {
    line: {
      accessTokenConfigured: boolean;
      accessTokenLast4: string | null;
      secretConfigured: boolean;
      secretLast4: string | null;
      verifiedAtIso: string | null;
    };
    email: {
      provider: 'brevo' | null;
      apiKeyConfigured: boolean;
      apiKeyLast4: string | null;
      senderEmail: string | null;
      senderName: string | null;
      verifiedAtIso: string | null;
    };
  };
  autoRules: {
    vipVisitCountThreshold: number;
    blacklistNoShowThreshold: number;
    publicReservationLeadTimeMinutes: number;
  };
  rateLimit: {
    publicReservationCapacity: number;
    publicReservationRefillPerSec: number;
  };
  capabilities: Record<string, unknown>;
  lineWebhookUrl: string;
  updatedAtIso: string;
  updatedBy: string;
}

async function settingsRequest<T>(
  method: 'GET' | 'PUT' | 'POST',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: CREDENTIALS,
    ...(body !== undefined
      ? {
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      : {}),
  };
  // Settings always go to the core worker (not BFF): writes would fail
  // through the read-only worker, and reads include decrypted metadata
  // we intentionally don't cache on the public edge.
  const res = await fetch(apiUrl(path), init);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'UNKNOWN',
      err.message ?? `${method} ${path}: HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export function getVenueSettings(venueId: string): Promise<VenueSettingsView> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('GET', `/api/staff/settings/venue-settings?${qs.toString()}`);
}

export function upsertVenueNotificationSettings(
  venueId: string,
  body: {
    line?: { channelAccessToken?: string | null; channelSecret?: string | null };
    email?: {
      provider?: 'brevo' | null;
      apiKey?: string | null;
      senderEmail?: string | null;
      senderName?: string | null;
    };
  },
): Promise<{ venueId: string; summary: VenueSettingsView['notification'] }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('PUT', `/api/staff/settings/notifications?${qs.toString()}`, body);
}

export function upsertVenueAutoRules(
  venueId: string,
  rules: Partial<VenueSettingsView['autoRules']>,
): Promise<{ venueId: string; rules: VenueSettingsView['autoRules'] }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('PUT', `/api/staff/settings/auto-rules?${qs.toString()}`, rules);
}

export function upsertVenueRateLimit(
  venueId: string,
  config: Partial<VenueSettingsView['rateLimit']>,
): Promise<{ venueId: string; config: VenueSettingsView['rateLimit'] }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('PUT', `/api/staff/settings/rate-limit?${qs.toString()}`, config);
}

export interface BrandingOverrideView {
  appTitle?: string;
  tagline?: string;
  tileUnitLabel?: string;
  availabilityHeadline?: string;
  availabilityFootnote?: string;
  availableWording?: string;
  fullWording?: string;
  bookingTerm?: string;
  slotTerm?: string;
  /** 6-digit hex colour, e.g. "#e05a1a". */
  primaryColor?: string;
  /** Absolute URL to the venue logo image. */
  logoUrl?: string;
  /** Google Fonts family name, e.g. "Zen Maru Gothic". */
  fontFamily?: string;
}

export function getEffectiveVenueProfile(venueId: string): Promise<{
  venueId: string;
  brandingOverride: BrandingOverrideView | null;
  updatedAtIso: string | null;
}> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('GET', `/api/staff/settings/effective-profile?${qs.toString()}`);
}

export function upsertVenueBranding(
  venueId: string,
  branding: BrandingOverrideView | null,
): Promise<{ venueId: string; branding: BrandingOverrideView | null; updatedAtIso: string }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('PUT', `/api/staff/settings/branding?${qs.toString()}`, { branding });
}

export type StaffRole = 'staff' | 'manager' | 'admin';

export interface SessionActor {
  actorId: string;
  role: StaffRole;
  email: string | null;
}

export async function fetchSessionActor(): Promise<SessionActor> {
  const res = await fetch(apiUrl('/api/staff/session'), withVenueHeader({ credentials: CREDENTIALS }));
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `session: HTTP ${res.status}`);
  }
  return (await res.json()) as SessionActor;
}

export interface StaffView {
  staffId: string;
  email: string;
  displayName: string;
  role: StaffRole;
  createdAtIso: string;
  updatedAtIso: string;
  revokedAtIso: string | null;
}

export function listStaff(
  venueId: string,
  opts: { includeRevoked?: boolean } = {},
): Promise<{ items: StaffView[] }> {
  const qs = new URLSearchParams({
    venueId,
    ...(opts.includeRevoked ? { includeRevoked: '1' } : {}),
  });
  return settingsRequest('GET', `/api/staff/staff?${qs.toString()}`);
}

export function inviteStaff(
  venueId: string,
  body: { email: string; displayName: string; role: StaffRole },
): Promise<{ staffId: string; email: string; role: StaffRole; createdAtIso: string }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('POST', `/api/staff/staff?${qs.toString()}`, body);
}

export function setStaffRole(
  staffId: string,
  role: StaffRole,
): Promise<{ staffId: string; role: StaffRole }> {
  return settingsRequest('PUT', `/api/staff/staff/${encodeURIComponent(staffId)}/role`, { role });
}

export function revokeStaff(staffId: string): Promise<{ staffId: string; revokedAtIso: string }> {
  return settingsRequest(
    'POST',
    `/api/staff/staff/${encodeURIComponent(staffId)}/revoke`,
    {},
  );
}

export interface ChargeListItem {
  chargeId: string;
  orderId: string;
  amount: number;
  currency: string;
  method: string;
  provider: string;
  providerTransactionId: string | null;
  status: string;
  refundedAmount: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  createdAtIso: string;
  authorizedAtIso: string | null;
  capturedAtIso: string | null;
  settledAtIso: string | null;
  failedAtIso: string | null;
}

export function listChargesApi(
  venueId: string,
  opts: { statuses?: readonly string[]; limit?: number; offset?: number } = {},
): Promise<{ items: ChargeListItem[] }> {
  const qs = new URLSearchParams({ venueId });
  if (opts.statuses && opts.statuses.length > 0) qs.set('statuses', opts.statuses.join(','));
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  const path = `/api/staff/charges?${qs.toString()}`;
  return new Promise((resolve, reject) => {
    fetch(readUrl(path), withVenueHeader({ credentials: CREDENTIALS }))
      .then(async (res) => {
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
          reject(new ApiError(res.status, err.code ?? 'UNKNOWN', err.message ?? `${path}: ${res.status}`));
          return;
        }
        resolve((await res.json()) as { items: ChargeListItem[] });
      })
      .catch(reject);
  });
}

export type DashboardPreset = 'today' | '7d' | '30d' | '90d';

export interface PaymentDashboardView {
  windowStartIso: string;
  windowEndIso: string;
  hero: {
    revenueAmount: number;
    capturedCount: number;
    approvalRate: number;
    avgTicketAmount: number;
    currency: string;
  };
  heroPrevious: {
    revenueAmount: number;
    capturedCount: number;
    approvalRate: number;
    avgTicketAmount: number;
    currency: string;
  };
  daily: Array<{
    dateIso: string;
    capturedAmount: number;
    capturedCount: number;
    refundedAmount: number;
    failedCount: number;
  }>;
  hourlyHeatmap: Array<{
    dayOfWeek: number;
    hour: number;
    count: number;
    amount: number;
  }>;
  methodBreakdown: Array<{ method: string; count: number; amount: number }>;
  providerBreakdown: Array<{ provider: string; count: number; amount: number }>;
  funnel: {
    initiated: number;
    authorized: number;
    captured: number;
    settled: number;
    failed: number;
    refunded: number;
    partiallyRefunded: number;
  };
  failures: Array<{
    errorCode: string;
    count: number;
    lastSeenIso: string | null;
    exampleMessage: string | null;
  }>;
  cycleTime: {
    samples: Array<{
      chargeId: string;
      method: string;
      provider: string;
      latencyMs: number;
      status: string;
      createdAtIso: string;
    }>;
    p50Ms: number | null;
    p85Ms: number | null;
    p95Ms: number | null;
    countCaptured: number;
  };
  refunds: {
    totalAmount: number;
    count: number;
    daily: Array<{ dateIso: string; amount: number; count: number }>;
    avgTatMs: number | null;
    pendingCount: number;
  };
  settlement: {
    daily: Array<{
      dateIso: string;
      perProvider: Array<{
        provider: string;
        gross: number;
        feeEstimate: number;
        netEstimate: number;
      }>;
    }>;
  };
  profitLoss: ProfitLossSummary;
}

export type ExpenseCategory =
  | 'rent'
  | 'hardware'
  | 'salary'
  | 'insurance'
  | 'software'
  | 'utility'
  | 'ingredient'
  | 'supplies'
  | 'marketing'
  | 'bonus'
  | 'other';

export type ExpenseKind = 'fixed' | 'variable';

export type ExpenseRecurrence = 'one_off' | 'weekly' | 'monthly' | 'yearly';

export interface ExpenseBreakdownItem {
  readonly category: ExpenseCategory;
  readonly amount: number;
  readonly kind: ExpenseKind;
}

export interface ProfitLossSummary {
  readonly grossRevenue: number;
  readonly gatewayFees: number;
  readonly refunds: number;
  readonly fixedCosts: number;
  readonly variableCosts: number;
  readonly netProfit: number;
  readonly netMargin: number;
  readonly currency: string;
  readonly expenseBreakdown: {
    readonly byCategory: readonly ExpenseBreakdownItem[];
    readonly totalAmount: number;
    readonly fixedAmount: number;
    readonly variableAmount: number;
    readonly itemCount: number;
  };
}

export interface ExpenseView {
  readonly expenseId: string;
  readonly category: ExpenseCategory;
  readonly kind: ExpenseKind;
  readonly amount: number;
  readonly currency: string;
  readonly recurrence: ExpenseRecurrence;
  readonly description: string | null;
  readonly effectiveFromIso: string;
  readonly effectiveToIso: string | null;
  readonly active: boolean;
  readonly createdAtIso: string;
}

export const EXPENSE_CATEGORY_LABEL_ZH: Readonly<Record<ExpenseCategory, string>> = {
  rent: '房租',
  hardware: '硬體攤提',
  salary: '員工薪資',
  insurance: '保險',
  software: '軟體訂閱',
  utility: '水電瓦斯',
  ingredient: '食材',
  supplies: '耗材',
  marketing: '行銷',
  bonus: '獎金',
  other: '其他',
};

export const EXPENSE_CATEGORY_DEFAULT_KIND: Readonly<Record<ExpenseCategory, ExpenseKind>> = {
  rent: 'fixed',
  hardware: 'fixed',
  salary: 'fixed',
  insurance: 'fixed',
  software: 'fixed',
  utility: 'variable',
  ingredient: 'variable',
  supplies: 'variable',
  marketing: 'variable',
  bonus: 'variable',
  other: 'variable',
};

export const EXPENSE_RECURRENCE_LABEL_ZH: Readonly<Record<ExpenseRecurrence, string>> = {
  one_off: '一次性',
  weekly: '每週',
  monthly: '每月',
  yearly: '每年',
};

export function listExpenses(
  venueId: string,
  includeArchived = false,
): Promise<{ items: ExpenseView[] }> {
  const qs = new URLSearchParams({ venueId });
  if (includeArchived) qs.set('includeArchived', '1');
  const path = `/api/staff/expenses?${qs.toString()}`;
  return new Promise((resolve, reject) => {
    fetch(readUrl(path), withVenueHeader({ credentials: CREDENTIALS }))
      .then(async (res) => {
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
          reject(
            new ApiError(
              res.status,
              err.code ?? 'UNKNOWN',
              err.message ?? `GET ${path}: HTTP ${res.status}`,
            ),
          );
          return;
        }
        resolve((await res.json()) as { items: ExpenseView[] });
      })
      .catch(reject);
  });
}

export interface CreateExpenseInput {
  readonly category: ExpenseCategory;
  readonly kind?: ExpenseKind;
  readonly amount: number;
  readonly recurrence: ExpenseRecurrence;
  readonly description?: string | null;
  readonly effectiveFromIso: string;
}

export function createExpense(
  venueId: string,
  body: CreateExpenseInput,
): Promise<{ expenseId: string; createdAtIso: string }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('POST', `/api/staff/expenses?${qs.toString()}`, body);
}

export function endDateExpense(
  expenseId: string,
  endDateIso: string,
): Promise<{ expenseId: string }> {
  return settingsRequest('POST', `/api/staff/expenses/${expenseId}/end-date`, { endDateIso });
}

export function getPaymentDashboard(
  venueId: string,
  preset: DashboardPreset = '30d',
): Promise<PaymentDashboardView> {
  const qs = new URLSearchParams({ venueId, preset });
  const path = `/api/staff/payments/dashboard?${qs.toString()}`;
  // Dashboard is read-only → prefer BFF for perf; fall back to core via readUrl.
  return new Promise((resolve, reject) => {
    fetch(readUrl(path), withVenueHeader({ credentials: CREDENTIALS }))
      .then(async (res) => {
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
          reject(
            new ApiError(
              res.status,
              err.code ?? 'UNKNOWN',
              err.message ?? `GET ${path}: HTTP ${res.status}`,
            ),
          );
          return;
        }
        resolve((await res.json()) as PaymentDashboardView);
      })
      .catch(reject);
  });
}

export interface PaymentGatewayView {
  providerKey: string;
  supportedMethods: readonly string[];
  enabled: boolean;
  setupHint?: string;
}

export function listPaymentGateways(
  venueId: string,
): Promise<{ items: PaymentGatewayView[] }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('GET', `/api/staff/settings/payment-gateways?${qs.toString()}`);
}

// ── POS + payment (§Region J) ──────────────────────────────────────────

export type PaymentMethod =
  | 'manual_cash'
  | 'manual_transfer'
  | 'credit_card'
  | 'line_pay'
  | 'apple_pay'
  | 'google_pay';

export interface OpenOrderResult {
  orderId: string;
  openedAtIso: string;
}

export function openOrder(
  venueId: string,
  body: { reservationId?: string | null; customerId?: string | null },
): Promise<OpenOrderResult> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('POST', `/api/staff/orders?${qs.toString()}`, body);
}

export interface AddOrderItemResult {
  orderId: string;
  itemId: string;
  lineTotalAmount: number;
  orderSubtotalAmount: number;
  orderTotalAmount: number;
}

export function addOrderItem(
  orderId: string,
  body: { menuItemId: string; quantity: number; note?: string | null; modifiers?: string[] },
): Promise<AddOrderItemResult> {
  return settingsRequest(
    'POST',
    `/api/staff/orders/${encodeURIComponent(orderId)}/items`,
    body,
  );
}

export interface SubmitOrderResult {
  orderId: string;
  status: 'submitted';
  subtotalAmount: number;
  discountTotalAmount: number;
  serviceChargeAmount: number;
  tipAmount: number;
  totalAmount: number;
  submittedAtIso: string;
}

export function submitOrder(
  orderId: string,
  body: { tipAmount?: number | null; serviceChargeOverride?: number | null } = {},
): Promise<SubmitOrderResult> {
  return settingsRequest(
    'POST',
    `/api/staff/orders/${encodeURIComponent(orderId)}/submit`,
    body,
  );
}

export function voidOrder(
  orderId: string,
  body: { reason: string },
): Promise<{ orderId: string; voidedAtIso: string }> {
  return settingsRequest(
    'POST',
    `/api/staff/orders/${encodeURIComponent(orderId)}/void`,
    body,
  );
}

export interface AuthorizeOrderPaymentResult {
  chargeId: string;
  status: 'authorized' | 'pending' | 'failed';
  providerTransactionId: string | null;
  redirectUrl?: string;
  errorCode?: string;
  errorMessage?: string;
}

export function authorizeOrderPayment(
  venueId: string,
  orderId: string,
  body: {
    method: PaymentMethod;
    preferredProvider?: string;
    providerPayload?: Record<string, unknown>;
    expectedAmount: number;
  },
): Promise<AuthorizeOrderPaymentResult> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest(
    'POST',
    `/api/staff/orders/${encodeURIComponent(orderId)}/payment/authorize?${qs.toString()}`,
    body,
  );
}

export function testLinePush(
  venueId: string,
  body: { recipientUserId: string; message?: string },
): Promise<{ result: string; detail: string | null }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('POST', `/api/staff/settings/test-line-push?${qs.toString()}`, body);
}

// ── Play session (玩樂結帳) ─────────────────────────────────────────

// 型別 SSOT: 從 @pricing 來（共用計價 domain）；rounding 還是 play-specific
// 所以直接 extend @pricing 的 PricingSnapshot，不再重新定義。
// 這個 re-export 讓 UI 層不需要跨 module boundary 多跳一次。
export type { StrategyParams as PlaySessionStrategyParams } from '@pricing';
import type { PricingSnapshot } from '@pricing';

export type PlaySessionRoundingType =
  | 'unit_hour_tolerant'
  | 'quarter_up'
  | 'half_up'
  | 'whole_hour_up'
  | 'per_minute';

export interface PlaySessionRateSnapshot extends PricingSnapshot {
  readonly rounding: {
    readonly type: PlaySessionRoundingType;
    readonly params: Record<string, unknown>;
  };
}

export interface PlaySessionView {
  readonly sessionId: string;
  readonly zoneId: string;
  readonly partySize: number;
  readonly reservationId: string | null;
  readonly customerId: string | null;
  readonly startedAtIso: string;
  readonly endedAtIso: string | null;
  readonly status: 'active' | 'ended' | 'cancelled';
  readonly finalAmount: number | null;
  readonly currentEstimate: number | null;
  readonly currency: string;
}

export function listPlaySessions(
  venueId: string,
  opts: { includeTerminal?: boolean; zoneId?: string } = {},
): Promise<{ items: readonly PlaySessionView[] }> {
  const qs = new URLSearchParams({ venueId });
  if (opts.includeTerminal) qs.set('includeTerminal', '1');
  if (opts.zoneId) qs.set('zoneId', opts.zoneId);
  const path = `/api/staff/play-sessions?${qs.toString()}`;
  return new Promise((resolve, reject) => {
    fetch(readUrl(path), withVenueHeader({ credentials: CREDENTIALS }))
      .then(async (res) => {
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
          reject(
            new ApiError(
              res.status,
              err.code ?? 'UNKNOWN',
              err.message ?? `GET ${path}: HTTP ${res.status}`,
            ),
          );
          return;
        }
        resolve((await res.json()) as { items: readonly PlaySessionView[] });
      })
      .catch(reject);
  });
}

export interface StartPlaySessionInput {
  readonly zoneId: string;
  readonly partySize: number;
  readonly reservationId?: string | null;
  readonly customerId?: string | null;
  readonly rateSnapshot: PlaySessionRateSnapshot;
}

export function startPlaySession(
  venueId: string,
  body: StartPlaySessionInput,
): Promise<{ sessionId: string; startedAtIso: string }> {
  const qs = new URLSearchParams({ venueId });
  return settingsRequest('POST', `/api/staff/play-sessions?${qs.toString()}`, body);
}

export function endPlaySession(
  sessionId: string,
): Promise<{
  sessionId: string;
  endedAtIso: string;
  finalAmount: number;
  billableMinutes: number;
}> {
  return settingsRequest('POST', `/api/staff/play-sessions/${sessionId}/end`);
}

export function cancelPlaySession(
  sessionId: string,
  reason: string,
): Promise<{ sessionId: string; cancelledAtIso: string }> {
  return settingsRequest('POST', `/api/staff/play-sessions/${sessionId}/cancel`, { reason });
}
